import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Clock, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ErpSyncAlert from "./ErpSyncAlert";
import { ErpReconciliation } from "./ErpReconciliation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const localTime = iso => new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });

export default function ErpSyncStatus({ onSynced }) {
  const { can } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [version, setVersion] = useState(0);
  const [report, setReport] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => axios.get(`${API}/erp/schedule`).then(r => setSchedule(r.data)), []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const patch = async data => {
    setSaving(true);
    try {
      const { data: saved } = await axios.put(`${API}/erp/schedule`, data);
      setSchedule(current => ({ ...current, ...saved }));
    } catch (error) { setNotice(error?.response?.data?.detail || "Senkron ayarı kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const checkAll = async () => {
    setChecking(true); setNotice("");
    try { const { data } = await axios.get(`${API}/erp/reconciliation`); setReport(data); }
    catch (error) { setReport(null); setNotice(error?.response?.data?.detail || "Geçmiş satışlar kontrol edilemedi"); }
    finally { setChecking(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    setNotice("");
    try {
      const { data } = await axios.post(`${API}/erp/sync-now`);
      setNotice(data.ok
        ? `Tüm geçmiş kontrol edildi · ${data.created_count} yeni, ${data.updated_count} güncellendi (${data.deactivated_count || 0} pasif)`
        : `Senkron hatası: ${data.error}`);
      await load();
      setReport(null);
      setVersion(v => v + 1);
      onSynced?.();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Senkron başarısız");
    } finally {
      setBusy(false);
    }
  };

  if (!schedule) return null;
  const last = schedule.last_run;
  const hour = String(schedule.hour).padStart(2, "0");

  return (
    <section className="panel sync-panel" data-testid="erp-sync-status">
      <div className="panel-head">
        <div>
          <p className="eyebrow">OTOMATİK SENKRON</p>
          <h3>Tüm satış geçmişi</h3>
          <small className="hint" data-testid="erp-sync-scope">
            Her gece {hour}:05 (İstanbul) · tüm dönemler · ciro, komisyon, satış adedi ve iptaller
          </small>
        </div>
        <div className="head-side">
          {can("groups") && (
            <label className="check-line">
              <input type="checkbox" data-testid="erp-sync-enabled" disabled={saving || busy} checked={schedule.enabled !== false}
                onChange={e => patch({ enabled: e.target.checked })}/>
              <span>Açık</span>
            </label>
          )}
          <button className="outline-btn" data-testid="erp-check-history" disabled={busy || checking} onClick={checkAll}>
            {checking ? <Loader2 size={15} className="spin"/> : <RefreshCw size={15}/>} Tüm geçmişi kontrol et
          </button>
          {can("create") && (
            <button className="outline-btn" data-testid="erp-sync-now" disabled={busy || checking} onClick={syncNow}>
              {busy ? <Loader2 size={15} className="spin"/> : <RefreshCw size={15}/>} Şimdi senkronize et
            </button>
          )}
        </div>
      </div>

      {can("groups") && (
        <div className="form-row">
          <div className="field">
            <label>Çalışma saati (İstanbul)</label>
            <select data-testid="erp-sync-hour" disabled={saving || busy} value={schedule.hour} onChange={e => patch({ hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:05`}</option>)}
            </select>
          </div>
          <div className="field"><label>Kapsam</label><strong data-testid="erp-history-coverage">Tüm dönemler — ay sınırı yok</strong></div>
        </div>
      )}

      {notice && <div className="notice" data-testid="erp-sync-notice">{notice}</div>}
      <ErpReconciliation report={report}/>

      <div className="sync-last" data-testid="erp-last-run">
        <Clock size={14}/>
        {last
          ? <span>Son çalışma <b>{localTime(last.started_at)}</b> ({last.trigger === "auto" ? "otomatik" : "elle"}) ·
              {last.ok ? ` ${last.created_count} yeni, ${last.updated_count} güncellendi (${last.deactivated_count || 0} pasif)` : ` HATA: ${last.error}`}</span>
          : <span>Henüz çalışmadı. İlk otomatik senkron bu gece {hour}:05'te.</span>}
      </div>

      <ErpSyncAlert refreshKey={version} onAck={onSynced}/>
    </section>
  );
}
